import escapeStringRegexp from "escape-string-regexp";

export const IN_STOCK = "in-stock";
export const UNAVAILABLE_IN_THIS_SET = "not available in this set";
export const UNAVAILABLE_IN_ANY_SET = "not available in any set";

export type SparsemOption = string | undefined;
export type SparsemStatus =
  | typeof IN_STOCK
  | typeof UNAVAILABLE_IN_THIS_SET
  | typeof UNAVAILABLE_IN_ANY_SET;
export type SparsemSearchToken = SparsemOption | null | undefined;
export type SparsemSearchResult = {
  [option: string]: SparsemStatus;
};

function maxBy<T>(list: T[], getScore: (object: T) => number): T | undefined {
  let maxScore = 0;
  let max: T | undefined = list[0];
  list.forEach((item) => {
    const score = getScore(item);
    if (score > maxScore) {
      maxScore = score;
      max = item;
    }
  });
  return max;
}

/**
 * Sparsem class cloned from `narvar/shopify-zero-retailer`. It allows you to
 * search availability status of a specify exchange option.
 */
export default class Sparsem {
  SEP = "\t";
  BLANK = "__BLANK__";
  arr: string[] = [];
  inp: SparsemOption[][] = [];
  opts: SparsemOption[][] = [];

  get num(): number {
    return this.opts.length;
  }

  setArr(availableItems: SparsemOption[][], optionsConfig: SparsemOption[][]) {
    this.inp = availableItems;
    this.opts = optionsConfig;
    this.arr = availableItems.map((x) =>
      x.map((y) => y ?? this.BLANK).join(this.SEP),
    );
  }

  search(options: SparsemSearchToken[] = []): SparsemSearchResult {
    const str = options
      .map((option, i) => {
        if (i >= this.num) return;
        if (option) return escapeStringRegexp(option);
        return ".{1,}";
      })
      .filter(Boolean)
      .join(this.SEP);

    const out = this.arr
      .filter((x) => x.match(new RegExp(`^${str}$`)))
      .map((x) => x.split(this.SEP));

    const missingIdx = options.indexOf(null);

    if (!this.opts[missingIdx]) return {};

    const result: SparsemSearchResult = this.opts[missingIdx].reduce(
      (ret, val) => {
        ret[val ?? ""] = out.some((x) => x[missingIdx] === val)
          ? IN_STOCK
          : this.inp.some((x) => x[missingIdx] === val)
            ? UNAVAILABLE_IN_THIS_SET
            : UNAVAILABLE_IN_ANY_SET;
        return ret;
      },
      {} as SparsemSearchResult,
    );

    return result;
  }

  select(options: SparsemSearchToken[]): SparsemOption[] | undefined {
    const result = maxBy<SparsemOption[]>(this.inp, (availableItem) =>
      availableItem.reduce((score, itemOption, index) => {
        const match = options[index] === itemOption;
        return match ? score + 1 : score;
      }, 0),
    );
    return result;
  }
}
